import { capitalize, has, toLower, toString, filter } from 'lodash';
import { schema } from 'normalizr';
import { formatDate, getContractStatus, isNone, noneToNull, replaceBrackets } from '../utils';
import { MINOS, SWTYPES, SYSARCH } from '../settings';

// We use this Normalizr schemas to transform API responses from a nested form
// to a flat form where repos and users are placed in `entities`, and nested
// JSON objects are replaced with their IDs. This is very convenient for
// consumption by reducers, because we can easily build a normalized tree
// and keep it updated as we fetch more data.

// Read more about Normalizr: https://github.com/paularmstrong/normalizr

// GitHub's API may return results with uppercase letters while the query
// doesn't contain any. For example, "someuser" could result in "SomeUser"
// leading to a frozen UI as it wouldn't find "someuser" in the entities.
// That's why we're forcing lower cases down there.

const aliasListSchema = new schema.Entity('orgs', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        key: value.orgAlias,
        title: `[${value.orgAlias}] ${value.orgName}`
    })
});

const componentsSchema = new schema.Entity('components', {}, {
    idAttribute: 'name',
    processStrategy: (value, parent, key) => ({
        key: value.name,
        title: value.name
    })
});

const orgsSchema = new schema.Entity('organisations', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        total: parent.total,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: val.orgAlias,
                orgAlias: val.orgAlias,
                orgName: val.orgName,
                phone: noneToNull(val.phone),
                email: noneToNull(val.email)
                }))
            : []
    })
});

const orgSchema = new schema.Entity('organisations', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        key: value.orgAlias,
        alias: value.orgAlias,
        name: value.orgName,
        addr: noneToNull(value.address),
        phone: noneToNull(value.phone),
        email: noneToNull(value.email),
        notes: noneToNull(value.notes),
        contracts: Array.isArray(value.contracts)
            ? value.contracts.map(contract => ({
                ...contract,
                expired: getContractStatus(contract.endDate)
            }))
            : []
    })
});

const orgsToImpersonateSchema = new schema.Entity('organisations', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => {
        return {
            alias: value.orgAlias,
            name: value.orgName,
            defaultOrg: value.defaultOrg == 1
        };
    }
});

const orgUsersSchema = new schema.Entity('users', {}, {
    idAttribute: "email",
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        orgName: parent.orgName,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: val.email,
                email: val.email,
                fullName: val.fullName,
                grantName: val.grantName,
                disabled: val.disabled == "1",
                expiryDate: val.expiryDate,
                defaultOrgName: val.defaultOrgName,
                orgAlias: val.defaultOrgAlias
            }))
            : []
    })
});

const orgIssuesSchema = new schema.Entity('issues', {}, {
    idAttribute: "ticketRef",
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        orgName: parent.orgName,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: val.ticketRef,
                ticketRef: val.ticketRef,
                summary: val.summary,
                submittedBy: replaceBrackets(val.submittedBy),
                submitDatetime: val.submitDatetime,
                status: val.status
            }))
            : []
    })
});

const orgLicensesSchema = new schema.Entity('licenses', {}, {
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        orgName: parent.orgName,
        result: value
    })
});

const usersSchema = new schema.Entity('users', {}, {
    idAttribute: "email",
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: val.email,
                email: val.email,
                fullName: val.fullName,
                grantName: val.grantName,
                orgAlias: val.defaultOrgAlias,
                defaultOrgName: val.defaultOrgName,
                disabled: val.disabled == "1",
                expiryDate: noneToNull(val.expiryDate)
            }))
            : []
    })
});

const userSchema = new schema.Entity('users', {}, {
    idAttribute: (value, parent, key) => toLower(value.email),
    processStrategy: (value, parent, key) => ({
        key: value.email,
        email: value.email,
        name: value.fullName,
        orgName: value.defaultOrgName,
        orgAlias: value.defaultOrgAlias,
        grant: value.grantName,
        disabled: value.disabled == "1",
        grants: value.grants
            ? value.grants
            : [{
                grantName: value.grantName,
                orgName: value.defaultOrgName,
                orgAlias: value.defaultOrgAlias,
                issuedBy: value.issuedBy,
                expiryDate: value.expiryDate
            }]
    })
});

const currentUserSchema = new schema.Entity('users', {}, {
    idAttribute:  (value, parent, key) => toLower(value.email),
    processStrategy: (value, parent, key) => ({
        email: toLower(value.email),
        fullName: value.fullName,
        lastSeenVersion: value.lastSeenVersion,
        adminRead: value.adminRead == 1,
        adminWrite: value.adminWrite == 1,
        auditRead: value.auditRead == 1,
        contractsRead: value.contractsRead == 1,
        contractsWrite: value.contractsWrite == 1,
        contractsMassUpdate: value.massUpdate == 1,
        jiraRead: value.jiraRead == 1,
        jiraWrite: value.jiraWrite == 1,
        login: value.login == 1,
        orgRead: value.orgRead == 1,
        orgWrite: value.orgWrite == 1,
        orgDelete: value.orgDelete == 1,
        packageRead: value.packageRead == 1,
        packageWrite: value.packageWrite == 1,
        shcRead: value.shcRead == 1,
        shcWrite: value.shcWrite == 1,
        t4Read: value.t4Read == 1,
        t4Write: value.t4Write == 1,
        userWrite: value.userWrite == 1,
        userDelete: value.userDelete == 1,
        defaultOrgName: value.orgName,
        orgAlias: value.orgAlias,
        isAdmin: value.isAdmin == 1,
        isVsiUser: value.defaultOrgAlias === "vsi",
        grant: value.grantName,
        canImpersonate: value.canImpersonate == 1,
        firstLogin: value.firstLogin == 1,
        licenseRead: value.licenseRead == 1
    })
});

const userSavedFiltersSchema = new schema.Entity('filters', {}, {});

const issueSchema = new schema.Entity('issues', {}, {
    idAttribute: (value, parent, key) => toLower(value.ticketRef)
});

const issueCommentsSchema = new schema.Entity('comments', {}, {
    idAttribute: "commentRef",
    processStrategy: (value, parent, key) => ({
        key: value.commentRef,
        created: value.submitDatetime,
        author: value.submittedBy,
        body: value.commentBody,
        visibility: value.visibility,
        edited: value.submitDatetime !== value.updateDatetime
    })
});

const orgContractsSchema = new schema.Entity('contracts', {}, {
    // idAttribute: (value, parent, key) => parent.indexOf(value), // "contractNumber",
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: parent.result.indexOf(val),
                contractName: val.contractName,
                contractNumber: val.contractNumber,
                endDate: val.endDate,
                orgName: val.orgName,
                orgAlias: val.orgAlias,
                registeredBy: val.registeredBy,
                updatedBy: val.updatedBy,
                ecokits: Number(val.ecokits),
                rToMinorRel: Number(val.rToMinorRel),
                expired: getContractStatus(val.endDate)
            }))
            : []
        })
});

const contractsSchema = new schema.Entity('contracts', {}, {
    idAttribute: 'contractName',
    processStrategy: (value, parent, key) => ({
        key: value.contractName,
        title: value.contractName,
        active: value.inactive == 0,
        ns: value.nsContract == 1
    })
});

const contractOrgsSchema = new schema.Entity('contracts', {}, {
    idAttribute: "orgAlias",
    processStrategy: (value, parent, key) => {
        return ({
            total: parent.total || 0,
            contractName: parent.contractName,
            result: Array.isArray(value) ? value : []
        })
    }
});

const contractSchema = new schema.Entity('contracts', {}, {
    idAttribute: "contractNumber"
});

const grantsSchema = new schema.Entity('grants', {}, {
    idAttribute: 'grantName',
    processStrategy: (value, parent, key) => ({
        key: value.grantName,
        title: capitalize(value.grantName.split("_").join(" ")),
        visible: value.visible == "1",
        vsiOnly: value.vsiOnly == "1"
    })
});

const productsListSchema = new schema.Entity('products', {}, {
    idAttribute: 'productNumber',
    processStrategy: (value, parent, key) => {
        return value.map(row => ({
            key: toString(row.productNumber),
            title: row.productName
        }));
    }
});

const uploadContractsSchema = new schema.Entity("contracts", {}, {
    idAttribute: (value, parent, key) => parent.indexOf(value),
    processStrategy: (value, parent, key) => ({
        key: parent.indexOf(value),
        ...value
    })
});

const manageContractsSchema = new schema.Entity('contracts', {}, {
    idAttribute: "contractNumber",
    processStrategy: (value, parent, key) => ({
        // key: value.contractNumber,
        name: value.contractName,
        active: value.inactive == 0,
        ...value,
    })
});

const packagesSchema = new schema.Entity('packages', {}, {
    idAttribute: 'packageNumber',
    processStrategy: (value, parent, key) => ({
        total: parent.total || 0,
        result: Array.isArray(value)
            ? value.map(val => ({
                key: val.packageNumber,
                name: val.name,
                version: val.version,
                sysarch: val.sysarch.toString(),
                swtype: val.swtype,
                minos: val.minos.toString(),
                verified: val.verified,
                releaseDate: val.releaseDate,
                restartRequired: val.restartRequired,
                downloaded: val.downloaded == 1,
                archived: val.archivedVersion == 1,
                size: Number.parseInt(val.downloadSize) || 0,
                files: typeof val.files == "string" ? val.files.split(",").map(f => Number.parseInt(f)).filter(o => !isNaN(o)) : []
            }))
            : []
    })
});

const packageSchema = new schema.Entity('packages', {}, {
    idAttribute: (value, parent, key) => toLower(value[0]["packageNumber"]),
    processStrategy: (value, parent, key) => {
        const details = Array.isArray(value) ? value[0] : value;
        return ({
            key: details.packageNumber,
            name: details.name,
            version: details.version,
            sysarch: details.sysarch,
            swtype: toLower(details.type),
            minos: details.minos,
            description: details.description,
            restart: details.restartRequired,
            ecoRating: details.ecoRating,
            prereq: Array.isArray(details.prereq) ? details.prereq : [],
            requiredFor: Array.isArray(details.requiredFor) ? details.requiredFor : [],
            contracts: Array.isArray(details.contracts) ? details.contracts : [],
            registeredBy: details.registeredBy,
            registeredDatetime: details.registeredDatetime,
            style: toLower(details.style),
            verified: details.verified,
            verifiedBy: details.verifiedBy,
            verifiedDate: details.verifiedDatetime,
            verifyComment: details.verifyComment,
            releaseDate: details.releaseDate,
            downloaded: details.downloaded == 1,
            archived: details.archivedVersion == 1,
            files: Array.isArray(details.files)
                ? details.files.map(val => ({
                    key: val.packageFileNumber,
                    name: val.filename,
                    md5: val.md5,
                    size: val.downloadSize,
                    uploaded: val.uploadedDatetime,
                    saveAs: val.saveAs
                }))
                : [],
            restrictions: Array.isArray(details.additionalRestrictions) ? details.additionalRestrictions[0] : {},
            documentationLink: isNone(details.documentationLink) ? null : details.documentationLink,
            product: noneToNull(details.productName)
        })
    }
});

const searchPackagesSchema = new schema.Entity('packages', {}, {
    idAttribute: "packageNumber",
    processStrategy: (value) => ({
        key: value.packageNumber,
        ...value
    })
});

const masterKitListSchema = new schema.Entity('packages', {}, {
    idAttribute: "packageNumber",
    processStrategy: (value, parent) => {
        if (Array.isArray(parent) && parent.length) {
            let result = {};
            filter(SWTYPES, sw => ["e", "l", "o"].includes(sw.key)).map((swtype) => {
                result[swtype.key] = {};
                let productsByType = filter(parent, o => toLower(o.swtype) == swtype.key);
                SYSARCH.map(s => s.key).map((sysarch) => {
                    let values = filter(productsByType, v => v.sysarch.includes(sysarch)).map(v => ({
                        key: v.packageNumber,
                        name: v.name,
                        sysarch: v.sysarch,
                        minos: v.osVersions.filter(value => MINOS.filter(os => os.sysarch.includes(sysarch)).map( os => os.key).includes(value)),
                        swtype: toLower(v.swtype),
                        releaseDate: v.releaseDate,
                        ecoRating: v.ecoRating,
                        restartRequired: v.restartRequired,
                        hasAccess: v.hasAccess == 1
                    }))
                        .filter(v => v.minos.length)
                        .sort((a,b) => new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime());
                    if (values.length) {
                        result[swtype.key][sysarch] = values;
                    }
                })
            })

            return result
        }
        return {}
    }
});

const auditDetailsSchema = new schema.Entity('audit', {}, {
    idAttribute: "auditNumber"
});

const auditPackageDetailsSchema = new schema.Entity('audit', {}, {
    idAttribute: "auditNumber",
    processStrategy: (value) => {
        if (has(value, "updatedFields")) {
            let newUpdatedFields = {};
            Object.keys(value.updatedFields).map(key => {
                newUpdatedFields[toLower(key)] = value.updatedFields[key];
            })
            value.updatedFields = newUpdatedFields;
        }
        return value;
    }
});

const shcResultsSchema = new schema.Entity('shcResults', {}, {
    idAttribute: 'uploadRef',
    processStrategy: (value, parent, key) => ({
        key: value.uploadRef,
        date: value.evidenceTimestamp,
        uploaded: value.uploadDatetime,
        file: value.fileName,
        node: value.nodeName,
        org: value.orgName,
        failed: Number(value.totalFailed),
        fired: Number(value.totalFired),
        passed: Number(value.totalPassed)
    })
});

const shcResultSchema = new schema.Entity('shcResult', {}, {
    idAttribute: (value, parent, key) => value.uploadRef,
    processStrategy: (value, parent, key) => ({
        data: value.shcResult,
        file: { key: value.uploadRef, ...value.uploadData }
    })
});

const shcUploadSchema = new schema.Entity('shc', {}, {
    idAttribute: "uploadRef"
});

const licenseSchema = new schema.Entity('licenses', {}, {
    // idAttribute: "contractNumber"
});

// Schemas for Github API responses.
export const schemas = {
    ALIASES: [aliasListSchema],
    COMPONENTS: [componentsSchema],
    organisations: [orgsSchema],
    ORGS_TO_IMPERSONATE: [orgsToImpersonateSchema],
    ORG: [orgSchema],
    ORG_USERS: [orgUsersSchema],
    ORG_ISSUES: [orgIssuesSchema],
    ORG_LICENSES: [orgLicensesSchema],
    users: [usersSchema],
    USER: [userSchema],
    CURRENT_USER: currentUserSchema,
    USER_SAVED_FILTERS: [userSavedFiltersSchema],
    GRANTS_LIST: [grantsSchema],
    ISSUE: issueSchema,
    ISSUE_COMMENTS: [issueCommentsSchema],
    // contracts: [orgContractsSchema],
    CONTRACTS_LIST: [contractsSchema],
    CONTRACT: contractSchema,
    UPLOAD_CONTRACTS: [uploadContractsSchema],
    MANAGE_CONTRACTS: [manageContractsSchema],
    CONTRACT_ORGS: [contractOrgsSchema],
    packages: [packagesSchema],
    PACKAGE: packageSchema,
    SEARCH_PACKAGES: [searchPackagesSchema],
    MASTER_KIT_LIST: [masterKitListSchema],
    AUDIT_DETAILS: auditDetailsSchema,
    AUDIT_PACKAGE_DETAILS: auditPackageDetailsSchema,
    SHC_RESULTS: [shcResultsSchema],
    SHC_RESULT: shcResultSchema,
    SHC_UPLOAD: shcUploadSchema,
    LISENSE: licenseSchema,
    PRODUCTS_LIST: [productsListSchema]
};
